import { getActivePinia } from "pinia";
import { PERMISSION_STORE_ID } from "@/stores/ids";
import type { LocationQuery, RouteLocationRaw, RouteParamsGeneric, RouteRecordRaw } from "vue-router";

/**
 * Old administration URLs and where they live now.
 *
 * Bookmarks, links in e-mails (the webhook-disabled mail) and every `router.push` that still
 * uses an old route name end up here. The mapping is pure so it can be tested without a router.
 */

export const SETTINGS_HOME = "/settings";
export const SETTINGS_SPACES = "/settings/spaces";
export const SETTINGS_MAILBOXES = "/settings/email/mailboxes";
export const SETTINGS_WEBHOOKS = "/settings/integrations/webhooks";
export const SETTINGS_SEVDESK = "/settings/integrations/sevdesk";
/** The Abgleich tab, home of the one-time import (#274). */
export const SETTINGS_SEVDESK_RECONCILE = SETTINGS_SEVDESK + "?tab=reconcile";

/**
 * The import run `runId` inside the Abgleich tab (#307), where the import is step 1 of the stepper. Without a run (or
 * for the old wizard's `new`) the tab shows the current run, or the start.
 */
export function sevdeskImportTarget(runId?: string | null): RouteLocationRaw {
    if (!runId || runId === "new") return { path: SETTINGS_SEVDESK, query: { tab: "reconcile" } };
    return { path: SETTINGS_SEVDESK, query: { tab: "reconcile", import: runId } };
}

/** `?tab=` of the old `/manager/account` page → new path. */
export const LEGACY_ACCOUNT_TABS: Readonly<Record<string, string>> = {
  members: "/settings/users",
  email_portals: SETTINGS_MAILBOXES,
  webhooks: SETTINGS_WEBHOOKS,
  time_tracking: "/settings/time-tracking",
  customizations: "/settings/contacts/fields",
  // The contact portal is managed on the contact itself now.
  portals: "/contacts",
};

/** Target of `/manager/account?tab=…`; an unknown or missing tab lands on the settings start. */
export function legacyAccountTarget(tab: unknown): string {
  const value = Array.isArray(tab) ? tab[0] : tab;
  if (typeof value === "string" && Object.prototype.hasOwnProperty.call(LEGACY_ACCOUNT_TABS, value)) {
    return LEGACY_ACCOUNT_TABS[value];
  }
  return SETTINGS_HOME;
}

/** Target of `/settings/teams[/:id[/:tab]]`, the space pages before the rename (#162). */
export function legacySpaceTarget(id?: string | null, tab?: string | null): string {
  if (!id) return SETTINGS_SPACES;
  return SETTINGS_SPACES + "/" + encodeURIComponent(id) + (tab ? "/" + encodeURIComponent(tab) : "");
}

/** Target of `/manager/webhooks[/…]`. */
export function legacyWebhookTarget(id?: string | null, deliveries = false): string {
  if (!id) return SETTINGS_WEBHOOKS;
  return SETTINGS_WEBHOOKS + "/" + encodeURIComponent(id) + (deliveries ? "/deliveries" : "");
}

/** The pages of a tech admin in navigation order, by route name. */
const TECH_ADMIN_PAGES: ReadonlyArray<[string, string]> = [
  ["SettingsMailboxes", SETTINGS_MAILBOXES],
  ["SettingsWebhooks", SETTINGS_WEBHOOKS],
  ["SettingsSevDesk", SETTINGS_SEVDESK],
];

/**
 * Where an unknown or not permitted settings URL goes. Space admins only have the team pages, so
 * a manager-only page sends them to their team list instead of the 404 page. A tech admin lands
 * on the first integration page the account has; with none of them, outside the settings — a
 * settings target would end on this fallback again.
 */
export function settingsFallbackTarget(roles: readonly string[], hasRoute: (name: string) => boolean): string {
  if (roles.includes("manager")) return SETTINGS_HOME;
  if (roles.includes("space_admin")) return SETTINGS_SPACES;
  if (roles.includes("tech_admin")) {
    return TECH_ADMIN_PAGES.find(([name]) => hasRoute(name))?.[1] ?? "/";
  }
  return SETTINGS_SPACES;
}

interface NamedRoute {
  name?: unknown;
  children?: NamedRoute[];
}

function collectRouteNames(routes: NamedRoute[], into: Set<string>): Set<string> {
  for (const route of routes) {
    if (typeof route.name === "string") into.add(route.name);
    if (route.children) collectRouteNames(route.children, into);
  }
  return into;
}

/**
 * `settingsFallbackTarget` for the signed-in person. Roles and permitted routes are read from the
 * active Pinia state instead of through the permission store module: that module imports the
 * route tree, so importing it from the routes would be an import cycle. Without a store yet: no
 * roles. Before the permitted routes are known every page counts as available.
 * The state field `roles` of the permission store is guarded by
 * src/router/modules/__tests__/settingsRoutes.test.ts (manager → /settings).
 */
export function currentSettingsFallback(): string {
  const state = getActivePinia()?.state.value as
    | Record<string, { roles?: unknown; routes?: unknown } | undefined>
    | undefined;
  const roles = state?.[PERMISSION_STORE_ID]?.roles;
  const routes = state?.[PERMISSION_STORE_ID]?.routes;
  const names = Array.isArray(routes) && routes.length > 0 ? collectRouteNames(routes as NamedRoute[], new Set()) : null;
  return settingsFallbackTarget(Array.isArray(roles) ? (roles as string[]) : [], (name) => names === null || names.has(name));
}

/** Drops the consumed `tab` from the query and keeps anything else (e.g. a search). */
function restQuery(query: LocationQuery): LocationQuery {
  const rest: LocationQuery = { ...query };
  delete rest.tab;
  return rest;
}

function param(to: { params: RouteParamsGeneric }, name: string): string | null {
  const value = to.params[name];
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

/**
 * Redirect records for the old URLs. They carry the old route names, so a `router.push` by an
 * old name that was missed still ends up on the right page. No roles: a redirect is harmless,
 * the target route decides about access.
 */
export const legacyRedirectRoutes: RouteRecordRaw[] = [
  {
    path: "/manager",
    name: "Manager",
    redirect: SETTINGS_HOME,
    meta: { hidden: true },
  },
  {
    path: "/manager/account",
    name: "ManagerAccount",
    redirect: (to) => ({ path: legacyAccountTarget(to.query.tab), query: restQuery(to.query) }),
    meta: { hidden: true },
  },
  {
    path: "/manager/teams",
    name: "ManagerTeamsList",
    redirect: SETTINGS_SPACES,
    meta: { hidden: true },
  },
  {
    // The space pages were /settings/teams… until the rename (#162). The old route names stay
    // here, so a missed `router.push({ name: "SettingsTeam" })` still lands on the space page.
    path: "/settings/teams",
    name: "SettingsTeams",
    redirect: (to) => ({ path: SETTINGS_SPACES, query: to.query }),
    meta: { hidden: true },
  },
  {
    path: "/settings/teams/:id/:tab?",
    name: "SettingsTeam",
    redirect: (to) => ({ path: legacySpaceTarget(param(to, "id"), param(to, "tab")), query: to.query }),
    meta: { hidden: true },
  },
  {
    path: "/manager/webhooks",
    name: "ManagerWebhooksList",
    redirect: SETTINGS_WEBHOOKS,
    meta: { hidden: true },
  },
  {
    path: "/manager/webhooks/new",
    name: "ManagerWebhookNew",
    redirect: SETTINGS_WEBHOOKS + "/new",
    meta: { hidden: true },
  },
  {
    path: "/manager/webhooks/:id",
    name: "ManagerWebhookEdit",
    redirect: (to) => legacyWebhookTarget(param(to, "id")),
    meta: { hidden: true },
  },
  {
    path: "/manager/webhooks/:id/deliveries",
    name: "ManagerWebhookDeliveries",
    redirect: (to) => legacyWebhookTarget(param(to, "id"), true),
    meta: { hidden: true },
  },
];
